<script setup>
// A MENSAGEM QUE NÃO FOI ENTREGUE (22/10/2026, Victor: "precisa ser claro para a pessoa que foi uma falha").
// Antes o aviso só aparecia quando a falha vinha com motivo, e o motivo só ao passar o mouse: com a Meta dizendo só
// "falhou", a bolha ficava rosada e muda (conversa 5051, 15:36). Agora toda mensagem que falhou diz, à vista, que não
// foi entregue, o porquê em português (ou "o WhatsApp não confirmou a entrega") e o botão "Reenviar".
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS } from './constants';

const props = defineProps({
  error: { type: String, default: '' },
});

const emit = defineEmits(['retry']);

const { status, createdAt, content, attachments } = useMessageContext();

const { t } = useI18n();

// Os códigos da Meta que a equipe encontra na API oficial viram frase
// (24/09/2026). 131047 é a janela de 24 h fechada; 131026, número sem WhatsApp.
const ERROS_DA_META = {
  131047: 'CONVERSATION.JANELA.ERRO_FORA_DA_JANELA',
  131026: 'CONVERSATION.JANELA.ERRO_SEM_WHATSAPP',
};
const erroLegivel = computed(() => {
  if (!props.error) return t('CONVERSATION.FALHA.SEM_MOTIVO');
  const codigo = Object.keys(ERROS_DA_META).find(c => props.error.includes(c));
  return codigo ? t(ERROS_DA_META[codigo]) : props.error;
});

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});
</script>

<template>
  <div class="falha" role="alert">
    <Icon icon="i-ph-warning-circle-fill" class="falha__ico" />
    <strong class="falha__tit">{{ t('CONVERSATION.FALHA.TITULO') }}</strong>
    <span class="falha__motivo">{{ erroLegivel }}</span>
    <button
      v-if="canRetry"
      type="button"
      class="falha__reenviar"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      @click="emit('retry')"
    >
      <Icon icon="i-ph-arrows-counter-clockwise" class="size-[13px]" />
      {{ t('CONVERSATION.FALHA.REENVIAR') }}
    </button>
  </div>
</template>

<style scoped>
.falha {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  max-width: 100%;
  font-size: var(--txt-nota, 12.5px);
  line-height: 1.35;
}

.falha__ico {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: rgb(var(--ruby-11));
}

.falha__tit {
  color: rgb(var(--ruby-11));
  font-weight: var(--peso-forte, 600);
}

.falha__motivo {
  color: rgb(var(--slate-11));
}

.falha__reenviar {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid rgb(var(--ruby-7));
  border-radius: 999px;
  background: transparent;
  color: rgb(var(--ruby-11));
  font-weight: var(--peso-forte, 600);
  cursor: pointer;
}

.falha__reenviar:hover:not(:disabled) {
  background: rgb(var(--ruby-3));
}

.falha__reenviar:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
